import { useEffect, useRef, Fragment, forwardRef, useState } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { useTheme } from '@emotion/react'
import qs from 'qs'
import to from 'await-to-js'
import { isEmpty } from 'lodash'
import { Formik, Form, useField, useFormikContext } from 'formik'
import {
  Section,
  Container,
  SectionTitle,
  Alert,
  Button,
} from 'chaoskit/src/components'
import { misc, text } from 'chaoskit/src/assets/styles/utility'
import * as yup from 'yup'
import FormikField from 'chaoskit/src/components/FormikField'
import axios from 'axios'
import { tealGreen } from 'chaoskit/src/assets/styles/utility/gradient'
import { fluidSize } from 'chaoskit/src/assets/styles/utility/misc'
import { useFlags } from 'gatsby-plugin-launchdarkly'

import { HomeHero, HomeHeroGrid } from '~partials/home/Hero'
import ldTrackList from '~helpers/launchdarkly-track.json'
import useLaunchDarklyClient from '~hooks/useLaunchDarklyClient'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import FreeSignUp from '~components/FreeSignUp'
import Image from '~components/Image'
import WeeklyLiveDemoForm from '~partials/weekly-live-demo/WeeklyLiveDemoForm'
import hero from '~media/get-started/hero.svg'
import CustomerLogos from '~partials/global/CustomerLogos'
import q1Company from '~media/get-started/1-company.svg'
import q1Myself from '~media/get-started/1-myself.svg'
import q2Reduce from '~media/get-started/2-reduce.svg'
import q2Time from '~media/get-started/2-time.svg'
import q2Ship from '~media/get-started/2-ship.svg'
import q2Build from '~media/get-started/2-build.svg'
import q3Level1 from '~media/get-started/3-level1.svg'
import q3Level2 from '~media/get-started/3-level2.svg'
import q3Level3 from '~media/get-started/3-level3.svg'
import q3Level4 from '~media/get-started/3-level4.svg'
import DirectionalArrowIcon from '~media/get-started/directional-arrow.svg'
import { getPaddedStickyMenuOffset } from '~styles/utility'
import ButtonLink from '~components/ButtonLink'
import useCountryCode from '~hooks/useCountryCode'
import track from '~helpers/track'
import DemoForm from '~components/demo/DemoForm'
import ArrowsDownNotifier from '~components/ArrowsDownNotifier'

const TOTAL_QUESTIONS = 5

const getStartedSchema = yup.object().shape({
  'bot-field': yup.string().max(0),
  evaluatingFor: yup.string().trim().required(),
  reliabilityGoal: yup
    .string()
    .trim()
    .when('evaluatingFor', {
      is: 'Company',
      then: (field) => field.required(),
    }),
  chaosEngineeringExperience: yup
    .string()
    .trim()
    .trim()
    .when('evaluatingFor', {
      is: 'Company',
      then: (field) => field.required(),
    }),
  countryCode: yup.string().trim(),
})

const getStartedInitialValues = {
  'form-name': 'get-started',
  'bot-field': '',
  evaluatingFor: '',
  reliabilityGoal: '',
  chaosEngineeringExperience: '',
  countryCode: '',
}

// Utility that handles scrolling to specific questions and containers within the page taking into account our sticky header offset
const scrollToRef = ({ theme, ref }) => {
  window.scrollTo({
    top:
      ref.current.getBoundingClientRect().top +
      window.scrollY -
      getPaddedStickyMenuOffset(theme),
    behavior: 'smooth',
  })
}

const FormProgress = ({ step }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        position: 'relative',
        width: 100,
        height: theme.space.small,
        background: theme.color.border.base,
        borderRadius: theme.space.small / 2,
        marginBottom: theme.space.base,

        '&::before': {
          content: "''",
          background: theme.color.primary.base,
          height: theme.space.small,
          borderRadius: theme.space.small / 2,
          width: (step / TOTAL_QUESTIONS) * 100,
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
        },
      }}
    />
  )
}

FormProgress.propTypes = {
  step: PropTypes.number.isRequired,
}

const OptionCopyBreak = () => {
  const theme = useTheme()

  return <br css={{ [theme.mq.small__max]: { display: 'none' } }} />
}

const Option = ({ name, option: { label, value, icon } }) => {
  const theme = useTheme()

  const props = { name, value, type: 'radio' }

  const [field] = useField(props)

  return (
    <div
      css={[
        {
          '--gs__question-image-size': '75px',

          position: 'relative',
          borderRadius: theme.borderRadius.base,
          textAlign: 'center',
          paddingTop: theme.space.large,
          paddingBottom: theme.space.large,
          paddingLeft: theme.space.base,
          paddingRight: theme.space.base,
          display: 'grid',
          gap: theme.space.base,
          gridTemplateRows: 'var(--gs__question-image-size) 1fr',
          transformOrigin: 'center center',
          transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
        },

        field.checked && {
          ...tealGreen(theme),
        },

        !field.checked && {
          boxShadow: `0 0 0 1px ${theme.color.border.base}`,

          '&:hover, &:focus': {
            transform: 'scale(1.025)',
          },
        },
      ]}
    >
      <Image
        css={[
          {
            height: 75,
            maxWidth: 75,
            display: 'block',
            margin: 'auto',
          },

          !field.checked && {
            filter: 'grayscale(1)',
          },
        ]}
        src={icon}
        alt=""
      />
      <div
        css={[
          text.heading(theme),
          {
            fontSize: theme.fontSize.medium,
            textTransform: 'uppercase',
          },

          !field.checked && {
            color: theme.fontColor.muted,
          },

          field.checked && {
            color: theme.contrast.base,
          },
        ]}
      >
        {label}
      </div>
      <input
        css={[
          {
            // 1. Position and resize the form control to always cover its container
            // 2. Required for Firefox for positioning to the left
            // 3. Required for Webkit to make `height` work
            // 4. Hide control

            // 1
            position: 'absolute',
            top: 0,
            zIndex: 1,
            width: '100%',
            height: '100%',
            // 2
            left: 0,
            // 3
            WebkitAppearance: 'none',
            // 4
            opacity: 0,
          },

          !field.checked && {
            cursor: 'pointer',
          },
        ]}
        {...field}
        onChange={(e) => {
          track('Answered question', {
            label: e.target.name,
          })

          // Fire off normal Formik `onChange` event
          field.onChange(e)
        }}
        type="radio"
      />
    </div>
  )
}

Option.propTypes = {
  name: PropTypes.string.isRequired,
  option: PropTypes.object.isRequired,
}

const DirectionalArrow = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        marginTop: theme.space.xlarge,
        textAlign: 'center',
      }}
      {...props}
    >
      <Image
        src={DirectionalArrowIcon}
        css={{ height: theme.height.xxsmall }}
        alt=""
      />
    </div>
  )
}

const Question = ({
  prompt,
  name,
  options,
  columns = '2',
  step,
  scrollToOnMount,
}) => {
  const theme = useTheme()
  const ref = useRef()

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (scrollToOnMount) {
      scrollToRef({ theme, ref })
    }
  }, [])
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <div ref={ref}>
      <FormProgress step={step} />
      <div
        css={{
          display: 'grid',
          gap: theme.space.large,

          [theme.mq.medium]: {
            gridTemplateColumns: '1fr 1.25fr',
            gap: theme.space.xlarge,
          },
        }}
      >
        <div css={[text.heading(theme), theme.fontSize.h2__fluid]}>
          {prompt}
        </div>
        <div
          css={{
            display: 'grid',
            gap: theme.space.base,
            alignSelf: 'start',

            [theme.mq.small]: {
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
            },
          }}
        >
          {options.map((option) => (
            <Option key={option.label} name={name} option={option} />
          ))}
        </div>
      </div>
    </div>
  )
}

Question.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.any.isRequired,
      value: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
    })
  ),
  name: PropTypes.string.isRequired,
  prompt: PropTypes.string.isRequired,
  columns: PropTypes.string,
  step: PropTypes.number.isRequired,
  scrollToOnMount: PropTypes.bool,
}

const EvaluatingFor = (props) => (
  <Question
    step={1}
    prompt="I'm exploring Gremlin and Chaos Engineering for:"
    name="evaluatingFor"
    options={[
      {
        label: 'My company',
        value: 'Company',
        icon: q1Company,
      },
      {
        label: 'Myself',
        value: 'Myself',
        icon: q1Myself,
      },
    ]}
    {...props}
  />
)

// @NOTE Pulling this out to a var so we can access it in the "Talk to sales" CTA scenario
const reliabilityGoalOptions = [
  {
    label: 'Reduce our incident count',
    value: 'ReduceIncidentCount',
    icon: q2Reduce,
  },
  {
    label: 'Shorten our time to resolution',
    value: 'ShortenTimeToResolution',
    icon: q2Time,
  },
  {
    label: 'Ship code faster without making breaking changes',
    value: 'ShipCodeFaster',
    icon: q2Ship,
  },
  {
    label: 'Build confidence ahead of a product launch',
    value: 'BuildConfidence',
    icon: q2Build,
  },
]

const ReliabilityGoal = (props) => (
  <Question
    step={2}
    prompt="What's your most important reliability goal at the moment?"
    name="reliabilityGoal"
    options={reliabilityGoalOptions}
    scrollToOnMount
    {...props}
  />
)

const ChaosEngineeringExperience = (props) => (
  <Question
    step={3}
    prompt="What's your level of experience with Chaos Engineering?"
    name="chaosEngineeringExperience"
    options={[
      {
        label: "I'm totally new to Chaos Engineering",
        value: 'New',
        icon: q3Level1,
      },
      {
        label: (
          <Fragment>
            I've done some research
            <OptionCopyBreak /> (but that's it)
          </Fragment>
        ),
        value: 'OnlyResearch',
        icon: q3Level2,
      },
      {
        label: "I've done some Chaos Engineering",
        value: 'Some',
        icon: q3Level3,
      },
      {
        label: 'I do Chaos Engineering regularly',
        value: 'Regularly',
        icon: q3Level4,
      },
    ]}
    scrollToOnMount
    {...props}
  />
)

const TalkToSales = forwardRef(
  ({ form, programId, thankYouMessage, reliabilityGoal, ...rest }, ref) => {
    const theme = useTheme()

    const { client } = useLaunchDarklyClient()
    const { getStartedDemoRequested: isFeatureFlagEnabled } = useFlags()

    const reliabilityGoalLabel = reliabilityGoalOptions
      .find((goal) => goal.value === reliabilityGoal)
      ?.label?.toLowerCase()
      .replace('our', 'your')

    return (
      <RecommendationWrapper
        ref={ref}
        recommendations={['free', 'liveDemo']}
        {...rest}
      >
        <div css={{ alignSelf: 'start' }}>
          <SectionTitle
            as="h2"
            align={{ medium: 'left' }}
            title={
              isFeatureFlagEnabled
                ? 'Get a personalized Chaos Engineering overview'
                : 'Get a personalized demo'
            }
            css={{
              marginBottom: `${theme.space.large}px !important`,
            }}
          />
          {isFeatureFlagEnabled ? (
            <Fragment>
              <p>
                Based on your answers, we’d like to set up a 1:1 call to learn
                more about your goals, share reliability best practices, and
                discuss how Gremlin can help {reliabilityGoalLabel}.
              </p>
              <p>Provide your information and we will be in touch very soon!</p>
            </Fragment>
          ) : (
            <Fragment>
              <p>
                Based on your answers, we’d like to set up a 1:1 call to learn
                more about your goals and show you a custom demo of how Gremlin
                can help {reliabilityGoalLabel}.
              </p>
              <p>
                Provide your information and we will be in touch to discuss next
                steps!
              </p>
            </Fragment>
          )}
        </div>
        <div>
          <DemoForm
            id={form}
            fieldValues={{ programID: programId }}
            thankYouMessage={thankYouMessage}
            onSuccess={() => {
              if (client) {
                client.track(ldTrackList.getStartedDemoRequested)
              }
            }}
          />
        </div>
      </RecommendationWrapper>
    )
  }
)

TalkToSales.propTypes = {
  form: PropTypes.string.isRequired,
  programId: PropTypes.string.isRequired,
  thankYouMessage: PropTypes.string.isRequired,
  reliabilityGoal: PropTypes.string.isRequired,
}

const RecommendationWrapper = forwardRef(
  ({ recommendations, ...rest }, ref) => {
    const theme = useTheme()

    return (
      <Section css={{ padding: '0 !important' }} ref={ref} {...rest}>
        <Container>
          <div
            css={[
              fluidSize({
                theme,
                property: 'paddingBottom',
                from: theme.space.large,
                to: theme.space.large * 2,
              }),
              {
                display: 'grid',
                gap: theme.space.large,

                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: theme.space.xlarge,
                },
              },
            ]}
            {...rest}
          />
          <NotWhatYouWereLookingFor show={recommendations} />
        </Container>
      </Section>
    )
  }
)

RecommendationWrapper.propTypes = {
  recommendations: PropTypes.array.isRequired,
}

const TryGremlinFree = forwardRef((props, ref) => {
  const theme = useTheme()

  return (
    <RecommendationWrapper
      ref={ref}
      recommendations={['demo', 'liveDemo']}
      {...props}
    >
      <div css={{ alignSelf: 'start' }}>
        <SectionTitle
          as="h2"
          align={{ medium: 'left' }}
          title="Try Gremlin for yourself"
          css={{
            marginBottom: `${theme.space.large}px !important`,
          }}
        />
        <p>
          Based on your answers, we recommend you create your own Gremlin Free
          account.
        </p>
        <p>
          Gremlin’s Chaos Engineering tools allow you to <strong>safely</strong>
          , <strong>securely</strong>, and <strong>simply</strong> inject
          failure into your systems to find weaknesses before they cause
          customer-facing issues.
        </p>
      </div>
      <div>
        <FreeSignUp title={null} />
      </div>
    </RecommendationWrapper>
  )
})

const JoinLiveDemo = forwardRef((props, ref) => {
  const theme = useTheme()

  return (
    <RecommendationWrapper
      ref={ref}
      recommendations={['free', 'demo']}
      {...props}
    >
      <div css={{ alignSelf: 'start' }}>
        <SectionTitle
          as="h2"
          align={{ medium: 'left' }}
          title="Join our next live demo"
          css={{
            marginBottom: `${theme.space.large}px !important`,
          }}
        />
        <p>
          Based on your answers, we’d like to invite you to our next live demo
          of Gremlin.
        </p>
        <p>
          You’ll be able to see Gremlin in action as one of our experts guides
          you through the platform. You’ll also have an opportunity to have your
          questions answered during the interactive Q&A segment.
        </p>
        <p>Provide your information to save your seat.</p>
      </div>
      <div>
        <WeeklyLiveDemoForm hideWrapper />
      </div>
    </RecommendationWrapper>
  )
})

const NotWhatYouWereLookingFor = ({ show }) => {
  const theme = useTheme()

  const OptionBox = (props) => (
    <div
      css={{
        background: theme.color.light.base,
        boxShadow: theme.boxShadow.base,
        border: theme.border.base,
        borderRadius: theme.borderRadius.base,
        padding: theme.space.large,
        textAlign: 'center',
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gap: theme.space.medium,
      }}
      {...props}
    />
  )

  const OptionHeader = (props) => (
    <h3 css={{ margin: 0 }} {...props} /> // eslint-disable-line jsx-a11y/heading-has-content
  )

  const OptionSubText = (props) => (
    <div
      css={{ fontSize: theme.fontSize.small, marginTop: theme.space.medium }}
      {...props}
    />
  )

  const Free = () => (
    <OptionBox>
      <OptionHeader>
        Create a free
        <OptionCopyBreak /> Gremlin account
      </OptionHeader>

      <section>
        <ButtonLink url="/gremlin-free-software/?ref=gs-alt" type="primary">
          Create now
        </ButtonLink>
        <OptionSubText>
          No credit card required.
          <OptionCopyBreak />
          Get up and running in less than 3 minutes.
        </OptionSubText>
      </section>
    </OptionBox>
  )

  const Demo = () => (
    <OptionBox>
      <OptionHeader>
        Get a customized
        <OptionCopyBreak /> demo of Gremlin
      </OptionHeader>

      <section>
        <ButtonLink url="/demo/?ref=gs-alt" type="primary">
          Request a demo
        </ButtonLink>
        <OptionSubText>
          Schedule time to speak
          <OptionCopyBreak />
          with our sales team.
        </OptionSubText>
      </section>
    </OptionBox>
  )

  const LiveDemo = () => (
    <OptionBox>
      <OptionHeader>
        Join our next
        <OptionCopyBreak /> live demo
      </OptionHeader>

      <section>
        <ButtonLink url="/weekly-live-demo/?ref=gs-alt" type="primary">
          Register now
        </ButtonLink>
        <OptionSubText>
          Every Wednesday
          <OptionCopyBreak />
          at 2:00p ET
        </OptionSubText>
      </section>
    </OptionBox>
  )

  return (
    <Section
      css={{
        background: theme.color.panel.base,
        borderTop: theme.border.base,
        position: 'relative',
        width: 'var(--rw)',
        left: '50%',
        right: '50%',
        marginLeft: 'calc(var(--rw) / -2)',
        marginRight: 'calc(var(--rw) / -2)',
        paddingLeft: theme.space.base,
        paddingRight: theme.space.base,
      }}
    >
      <Container>
        <SectionTitle title="Not what you were looking for?" />
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
          }}
        >
          {show.includes('free') && <Free />}
          {show.includes('demo') && <Demo />}
          {show.includes('liveDemo') && <LiveDemo />}
        </div>
      </Container>
    </Section>
  )
}

NotWhatYouWereLookingFor.propTypes = {
  /* Determines which "other" panels to show after results form */
  show: PropTypes.array.isRequired,
}

const FormikAutoSubmit = () => {
  // Grab values and submitForm from context
  const { errors, submitForm } = useFormikContext()

  useEffect(() => {
    // Submit the form imperatively as an effect as soon as we have a valid form
    if (isEmpty(errors)) {
      submitForm()
    }
  }, [errors, submitForm])

  return null
}

const GetStarted = ({
  data: {
    pageData: {
      childMdx: { frontmatter: pageData },
    },
    demo: {
      childMdx: {
        frontmatter: {
          form: { embed: demoForm, programId, thankYouMessage },
        },
      },
    },
  },
}) => {
  const theme = useTheme()
  const countryCode = useCountryCode()

  const { client } = useLaunchDarklyClient()
  const { getStartedDemoRequested: isFeatureFlagEnabled } = useFlags()

  const contentRef = useRef()
  const recommendationsVisiblity = useRef({
    talkToSales: false,
    joinLiveDemo: false,
    tryGremlinFree: false,
  })

  const [response, setResponse] = useState()

  // This is called after our form has submitted so we can show the appropriate form/section
  const showResultsForm = (values) => {
    // They are evaluating Gremlin for themselves
    if (values.evaluatingFor === 'Myself') {
      recommendationsVisiblity.current.tryGremlinFree = true
    } else if (['CN', 'IN', 'BR'].includes(values.countryCode)) {
      // Clearbit Reveal endpoint did return something and it equals one of the following countries: China, India, or Brazil
      recommendationsVisiblity.current.joinLiveDemo = true
    } else {
      // Clearbit Reveal endpoint didn't return anything
      recommendationsVisiblity.current.talkToSales = true
    }
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)

    // Stubs in value for `countryCode` if available
    const mergedData = { ...values, countryCode }

    const [, success] = await to(
      axios({
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: qs.stringify(mergedData),
        url: '/',
      })
    )

    setSubmitting(false)

    if (success) {
      setResponse('success')

      showResultsForm(mergedData)

      // Fire off Segment event
      if (window.analytics) {
        delete mergedData['bot-field']
        delete mergedData['form-name']

        track('Submitted questionnare', {
          data: mergedData,
          label: 'netlify-get-started',
        })
      }

      // Scroll to content container since they may be a bit further down the page
      scrollToRef({ theme, ref: contentRef })
    } else {
      setResponse('error')
    }
  }

  useEffect(() => {
    if (client) {
      track('Get started: Demo requested', {
        label: isFeatureFlagEnabled ? 'Variation' : 'Default',
      })
    }
  }, [client, isFeatureFlagEnabled])

  return (
    <BaseLayout
      headerProps={{ showCTA: false }}
      footerProps={{ hideCTA: true }}
    >
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <HomeHero>
        <HeroContent>
          <HomeHeroGrid
            css={{
              [theme.mq.medium]: {
                gap: theme.space.large,
              },
            }}
          >
            <div
              css={{
                [theme.mq.small__max]: {
                  img: {
                    width: 300,
                  },
                },

                [theme.mq.medium]: {
                  gridRow: 1,
                  gridColumn: '1 / span 6',
                  direction: 'rtl',

                  img: {
                    maxWidth: 'none',
                    height: 450,
                  },
                },
              }}
            >
              <Image src={hero} alt="" />
            </div>
            <div
              css={{
                position: 'relative',
                zIndex: 1,

                [theme.mq.medium]: {
                  gridRow: 1,
                  gridColumn: '7 / span 6',
                },
              }}
            >
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data markdown>{pageData.hero.content}</Data>
              </HeroTitleSub>
              {!response && (
                <Button
                  type="reset"
                  css={{
                    marginTop: theme.space.large,
                  }}
                  onClick={() => {
                    scrollToRef({ theme, ref: contentRef })
                  }}
                >
                  <ArrowsDownNotifier />
                </Button>
              )}
            </div>
          </HomeHeroGrid>
        </HeroContent>
      </HomeHero>
      <Section css={{ paddingBottom: '0 !important' }} ref={contentRef}>
        <Container>
          {response === 'success' && (
            <CustomerLogos css={{ paddingTop: '0 !important' }} />
          )}
          <Formik
            validateOnMount
            initialValues={getStartedInitialValues}
            validationSchema={getStartedSchema}
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting }) => (
              <Fragment>
                {response === 'error' && (
                  <Section css={{ paddingTop: '0 !important' }}>
                    <Alert type="danger">
                      There was an error submitting your request. Please try
                      again later.
                    </Alert>
                  </Section>
                )}
                {!response && (
                  <Form
                    css={[
                      fluidSize({
                        theme,
                        property: 'paddingBottom',
                        from: theme.space.large,
                        to: theme.space.large * 2,
                      }),

                      isSubmitting && {
                        opacity: theme.opacity.base,
                        pointerEvents: 'none',
                      },
                    ]}
                  >
                    <FormikAutoSubmit />
                    <input type="hidden" name="form-name" value="get-started" />
                    <FormikField
                      name="bot-field"
                      css={misc.hide}
                      as="input"
                      autoComplete="new-password"
                    />
                    <div
                      css={{
                        display: 'grid',
                        gap: theme.space.xlarge,

                        [theme.mq.medium]: {
                          gap: theme.space.xlarge * 2,
                        },
                      }}
                    >
                      <EvaluatingFor />
                      {values.evaluatingFor === 'Company' && (
                        <Fragment>
                          <ReliabilityGoal />
                          {values.reliabilityGoal && (
                            <ChaosEngineeringExperience />
                          )}
                        </Fragment>
                      )}
                    </div>

                    <DirectionalArrow
                      css={{
                        filter: theme.color.border.filter,
                      }}
                    />
                  </Form>
                )}

                {recommendationsVisiblity.current.tryGremlinFree && (
                  <TryGremlinFree />
                )}
                {recommendationsVisiblity.current.joinLiveDemo && (
                  <JoinLiveDemo />
                )}
                {recommendationsVisiblity.current.talkToSales && (
                  <TalkToSales
                    form={demoForm}
                    fieldValues={{ programID: programId }}
                    thankYouMessage={thankYouMessage}
                    reliabilityGoal={values.reliabilityGoal}
                  />
                )}

                {/* @NOTE Useful for debugging purposes if we need this later

                <pre>{JSON.stringify(values, null, 2)}</pre>
                <pre>{JSON.stringify(errors, null, 2)}</pre>

                */}
              </Fragment>
            )}
          </Formik>
          {/* We need this static form here even though it's done with React state since Netlify requires the static HTML to be present on the page in order to parse data correctly 🤷‍♂️ */}
          <form
            css={misc.hide}
            name="get-started"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
          >
            <input type="hidden" name="form-name" value="get-started" />

            <input type="text" name="evaluatingFor" />
            <input type="text" name="reliabilityGoal" />
            <input type="text" name="chaosEngineeringExperience" />
            <input type="text" name="countryCode" />
            <input name="bot-field" />
          </form>
        </Container>
      </Section>
    </BaseLayout>
  )
}

GetStarted.propTypes = {
  data: PropTypes.object.isRequired,
}

export default GetStarted

export const pageQuery = graphql`
  query GetStartedPageData {
    pageData: file(
      name: { eq: "get-started" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            heading
            content
          }
        }
      }
    }

    demo: file(
      name: { eq: "demo" }
      absolutePath: { regex: "/src/demo-landing-pages/" }
    ) {
      childMdx {
        frontmatter {
          form {
            embed
            programId
            thankYouMessage
          }
        }
      }
    }
  }
`
